import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Heart } from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { supabase } from '../../lib/supabase';
import BottomNavigation from '../../components/Layout/BottomNavigation';
import Card from '../../components/UI/Card';
import type { Match, Profile } from '../../lib/supabase';

const MatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSupabaseAuth();
  const [matches, setMatches] = useState<(Match & { profile: Profile })[]>([]);
  const [loading, setLoading] = useState(true);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [isAuthenticated, navigate]);

  // Load user matches
  useEffect(() => {
    if (user) {
      loadMatches();
    }
  }, [user]);

  const loadMatches = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('matches')
        .select(`
          *,
          profile1:profiles!matches_user1_id_fkey(*),
          profile2:profiles!matches_user2_id_fkey(*)
        `)
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const userMatches = (data || [])
        .filter(match => match.profile1 && match.profile2) // Filter out matches with missing profiles
        .map(match => ({
          ...match,
          profile: match.user1_id === user.id ? match.profile2 : match.profile1
        }));

      setMatches(userMatches);
    } catch (error) {
      console.error('Error loading matches:', error);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = async (matchProfile: Profile) => {
    if (!user) return;
    
    // Navigate to chat with this user
    navigate(`/chat/${matchProfile.user_id}`);
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pb-20">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-white mb-4">Matches</h1>
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="p-4">
        <h1 className="text-2xl font-bold text-white mb-4">Your Matches</h1>
        
        {matches.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="text-6xl mb-4">💕</div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              No Matches Yet
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Keep swiping to find your perfect match!
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full hover:shadow-lg transition-all duration-200"
            >
              Start Swiping
            </button>
          </Card>
        ) : (
          <div className="space-y-3">
            {matches.map((match) => (
              <Card
                key={match.id}
                onClick={() => handleChatClick(match.profile)}
                className="p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img
                      src={match.profile.photos[0] || 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=200'}
                      alt={match.profile.first_name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                      <Heart size={12} className="text-white" fill="currentColor" />
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                        {match.profile.first_name} {match.profile.last_name}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(match.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {match.profile.bio || 'Start a conversation!'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-purple-600 dark:text-purple-400">
                        Age: {match.profile.age}
                      </span>
                      {match.profile.location_city && (
                        <>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {match.profile.location_city}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <MessageCircle className="text-purple-500" size={20} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default MatchesPage;